import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Sudoku — single-file JSX, no TypeScript, no Tailwind.
 * - 20 rotating examples (Previous / Next Example)
 * - Pencil notes, keyboard control
 * - Mistake highlighting + end banner (Solved / Incorrect)
 * - Bright givens
 */

const N = 9;
const DIGITS = [1,2,3,4,5,6,7,8,9];

/* ---------------- Styles (inline CSS) ---------------- */
const styles = {
  page: { minHeight: "100vh", background: "#f8fafc", color: "#0f172a", padding: 16, boxSizing: "border-box", fontFamily: "Inter, system-ui, Avenir, Helvetica, Arial, sans-serif" },
  container: { maxWidth: 920, margin: "0 auto" },
  header: { display: "flex", gap: 12, alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap" },
  h1: { fontSize: 28, fontWeight: 800, margin: 0 },
  hint: { fontSize: 13, color: "#475569", marginTop: 4 },
  kbd: { padding: "2px 6px", background: "#e2e8f0", borderRadius: 6, margin: "0 6px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" },
  btn: { padding: "8px 12px", borderRadius: 12, border: "1px solid #e2e8f0", background: "white", boxShadow: "0 1px 2px rgba(0,0,0,0.06)", cursor: "pointer", fontSize: 14 },
  btnPrimary: { background: "#4f46e5", color: "white", borderColor: "#4f46e5" },
  btnWarn: { background: "#f59e0b", color: "white", borderColor: "#f59e0b" },
  section: { marginTop: 20, display: "grid", gridTemplateColumns: "auto 1fr", gap: 24, alignItems: "start" },
  boardOuter: { outline: "none" },
  board: { display: "inline-grid", gridTemplateColumns: "repeat(9, 1fr)", background: "#e2e8f0", padding: 6, borderRadius: 16, boxShadow: "0 6px 18px rgba(0,0,0,0.08)" },
  cellBtn: { position: "relative", width: 56, height: 56, background: "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  bigNum: { fontSize: 20, fontWeight: 700, color: "#334155" },
  bigNumGiven: { color: "#92400e", fontWeight: 800 },
  selected: { boxShadow: "0 0 0 2px #6366f1 inset", background: "#eef2ff" },
  givenBg: { background: "#fef3c7" },
  mistakeRing: { boxShadow: "0 0 0 2px #ef4444 inset", color: "#b91c1c" },
  numpadWrap: { marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" },
  numpadBtn: { width: 48, height: 48, borderRadius: 12, background: "white", border: "1px solid #e2e8f0", boxShadow: "0 1px 2px rgba(0,0,0,0.06)", fontSize: 16, fontWeight: 600, cursor: "pointer" },
  clearBtn: { padding: "0 16px", height: 48, borderRadius: 12, border: "1px solid #e2e8f0", background: "white", cursor: "pointer" },
  pencilBtn: { padding: "0 16px", height: 48, borderRadius: 12, border: "1px solid #e2e8f0", background: "white", cursor: "pointer" },
  sidebar: { position: "sticky", top: 16, width: 260, background: "white", border: "1px solid #e2e8f0", borderRadius: 16, boxShadow: "0 8px 20px rgba(0,0,0,0.06)", padding: 16 },
  legendDot: (bg, border) => ({ display: "inline-block", width: 16, height: 16, borderRadius: 6, background: bg, border: `1px solid ${border}` }),
  notesGrid: (dim) => ({ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, width: "100%", height: "100%", padding: 6, opacity: dim ? 0.7 : 1 }),
  noteText: (visible) => ({ fontSize: 10, lineHeight: 1, color: visible ? "#334155" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }),
  footer: { marginTop: 18, textAlign: "center", color: "#94a3b8", fontSize: 12 },
  bannerWrap: { marginTop: 12, marginBottom: -8 },
  bannerOk: { padding: "10px 14px", borderRadius: 12, background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0", fontWeight: 600 },
  bannerBad: { padding: "10px 14px", borderRadius: 12, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", fontWeight: 600 },
  subline: { fontSize: 12, color: "#64748b" }
};

/* ---------------- Base example + 20 variants ----------------
   We keep one well-known base puzzle (81 chars; 0 = empty) and
   produce 20 examples by permuting digits 1..9 (bijective maps).
---------------------------------------------------------------- */
const BASE_PZL = "000260701680070090190004500820100040004602900050003028009300074040050036703018000";

/** Build a deterministic digit permutation for k = 0..19 */
function permMapFor(k) {
  // start with [1..9]
  const a = [1,2,3,4,5,6,7,8,9];
  // rotate by s
  const s = k % 9;
  const rot = a.slice(s).concat(a.slice(0,s));
  // occasionally reverse
  if ((k % 4) === 1) rot.reverse();
  // swap pairs depending on k to diversify
  if ((k % 5) === 0) [rot[0], rot[1]] = [rot[1], rot[0]];
  if ((k % 7) === 0) [rot[3], rot[4]] = [rot[4], rot[3]];
  // return mapping from digit -> permuted digit
  const map = {};
  for (let i = 0; i < 9; i++) map[a[i]] = rot[i];
  return map;
}

/** Apply a mapping to BASE_PZL to create a variant string */
function variantString(k) {
  const m = permMapFor(k);
  let out = "";
  for (let i = 0; i < 81; i++) {
    const ch = BASE_PZL[i];
    if (ch === "0") out += "0";
    else out += String(m[parseInt(ch,10)]);
  }
  return out;
}

/** Parse an 81-char puzzle string into our board model */
function parsePuzzle(str) {
  const b = [];
  for (let r = 0; r < 9; r++) {
    const row = [];
    for (let c = 0; c < 9; c++) {
      const v = parseInt(str[r*9 + c], 10);
      row.push({
        value: v || 0,
        notes: new Set(),
        given: v !== 0
      });
    }
    b.push(row);
  }
  return b;
}

/* ----------- Conflict detection (for mistakes + end banner) ---------- */
function cellKey(r, c) { return `${r}-${c}`; }
function thickBorder(r, c) {
  const top = r % 3 === 0 ? "2px solid #94a3b8" : "1px solid #cbd5e1";
  const left = c % 3 === 0 ? "2px solid #94a3b8" : "1px solid #cbd5e1";
  const bottom = r === 8 ? "2px solid #94a3b8" : "1px solid #cbd5e1";
  const right = c === 8 ? "2px solid #94a3b8" : "1px solid #cbd5e1";
  return { borderTop: top, borderLeft: left, borderBottom: bottom, borderRight: right };
}
function findConflicts(board) {
  const bad = new Set();
  // rows
  for (let r = 0; r < 9; r++) {
    const seen = {};
    for (let c = 0; c < 9; c++) {
      const v = board[r][c].value;
      if (!v) continue;
      if (seen[v] !== undefined) {
        bad.add(cellKey(r, c));
        bad.add(cellKey(r, seen[v]));
      } else seen[v] = c;
    }
  }
  // cols
  for (let c = 0; c < 9; c++) {
    const seen = {};
    for (let r = 0; r < 9; r++) {
      const v = board[r][c].value;
      if (!v) continue;
      if (seen[v] !== undefined) {
        bad.add(cellKey(r, c));
        bad.add(cellKey(seen[v], c));
      } else seen[v] = r;
    }
  }
  // boxes
  for (let br = 0; br < 3; br++) {
    for (let bc = 0; bc < 3; bc++) {
      const seen = {};
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
          const r = br * 3 + i;
          const c = bc * 3 + j;
          const v = board[r][c].value;
          if (!v) continue;
          if (seen[v]) {
            const [pr, pc] = seen[v];
            bad.add(cellKey(r, c));
            bad.add(cellKey(pr, pc));
          } else seen[v] = [r, c];
        }
      }
    }
  }
  return bad;
}

/* ---------------- Component ---------------- */
export default function SudokuApp() {
  // prepare 20 example strings (variants of base)
  const EXAMPLES = useMemo(() => {
    const arr = [];
    for (let k = 0; k < 20; k++) arr.push(variantString(k));
    return arr;
  }, []);

  const [idx, setIdx] = useState(0); // which example we’re on (0..19)
  const [board, setBoard] = useState(() => parsePuzzle(variantString(0)));
  const [selected, setSelected] = useState([0, 0]);
  const [pencil, setPencil] = useState(false);
  const [showGridLines, setShowGridLines] = useState(true);
  const [highlightStable, setHighlightStable] = useState(true);
  const [showMistakes, setShowMistakes] = useState(true);

  const boardRef = useRef(null);
  const [sr, sc] = selected;

  // focus for keyboard
  useEffect(() => {
    if (boardRef.current) {
      boardRef.current.tabIndex = 0;
      boardRef.current.focus();
    }
  }, [idx]);

  // keyboard
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const isTypingTarget = (t) => t && (t.isContentEditable || ["INPUT","TEXTAREA","SELECT"].includes(t.tagName));
    function onKeyDown(e) {
      if (isTypingTarget(e.target)) return;
      const k = typeof e.key === "string" ? e.key : "";
      if (!k) return;

      if (k === "ArrowUp") { setSelected(([r,c]) => [Math.max(0, r-1), c]); e.preventDefault(); return; }
      if (k === "ArrowDown") { setSelected(([r,c]) => [Math.min(8, r+1), c]); e.preventDefault(); return; }
      if (k === "ArrowLeft") { setSelected(([r,c]) => [r, Math.max(0, c-1)]); e.preventDefault(); return; }
      if (k === "ArrowRight") { setSelected(([r,c]) => [r, Math.min(8, c+1)]); e.preventDefault(); return; }

      if ((k >= "1" && k <= "9") || k === "0" || k === "Backspace" || k === "Delete") {
        const num = k >= "1" && k <= "9" ? parseInt(k, 10) : 0;
        handleInput(num);
        e.preventDefault();
        return;
      }

      if (k === "n" || k === "N") { setPencil((p) => !p); e.preventDefault(); }
      if (k === "m" || k === "M") { setShowMistakes((m) => !m); e.preventDefault(); }
    }
    el.addEventListener("keydown", onKeyDown);
    return () => el.removeEventListener("keydown", onKeyDown);
  }, [sr, sc, pencil, board]);

  function loadExample(newIndex) {
    const i = ((newIndex % 20) + 20) % 20;
    setIdx(i);
    setBoard(parsePuzzle(EXAMPLES[i]));
    setSelected([0,0]);
    setPencil(false);
  }
  function nextExample() { loadExample(idx + 1); }
  function prevExample() { loadExample(idx - 1); }
  function reloadExample() { loadExample(idx); }

  function handleInput(num) {
    setBoard((prev) => {
      const copy = prev.map((row) =>
        row.map((cell) => ({ value: cell.value, given: cell.given, notes: new Set(cell.notes) }))
      );
      const cell = copy[sr][sc];
      if (cell.given) return copy;
      if (pencil) {
        if (num === 0) cell.notes.clear();
        else {
          if (cell.notes.has(num)) cell.notes.delete(num);
          else cell.notes.add(num);
        }
      } else {
        cell.value = num; // 0 clears
        cell.notes.clear();
      }
      return copy;
    });
  }

  const onClickDigit = (n) => handleInput(n);
  const clearCell = () => handleInput(0);

  // derived
  const conflicts = useMemo(() => findConflicts(board), [board]);
  const filled = useMemo(() => {
    for (let r = 0; r < 9; r++) for (let c = 0; c < 9; c++) if (board[r][c].value === 0) return false;
    return true;
  }, [board]);
  const solved = filled && conflicts.size === 0;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.h1}>Sudoku </h1>
            <p style={styles.hint}>
              Select a cell → 1–9 to set, 0/⌫ to clear. Press
              <kbd style={styles.kbd}>N</kbd> for ✏️ Pencil,
              <kbd style={styles.kbd}>M</kbd> to toggle Mistakes.
              <span style={{ marginLeft: 8, ...styles.subline }}>Example {idx + 1} / 20</span>
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={styles.btn} onClick={prevExample}>◀︎ Previous</button>
            <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={nextExample}>Next ▶︎</button>
            <button style={styles.btn} onClick={reloadExample} title="Reload current">
              Reload
            </button>
            <button
              style={{ ...styles.btn, ...(pencil ? styles.btnPrimary : {}) }}
              onClick={() => setPencil((p) => !p)}
              title="Toggle Pencil mode (N)"
            >
              ✏️ Pencil {pencil ? "On" : "Off"}
            </button>
            <button style={styles.btn} onClick={() => setShowGridLines((s) => !s)}>
              {showGridLines ? "Hide" : "Show"} grid
            </button>
            <button
              style={{ ...styles.btn, ...(highlightStable ? styles.btnWarn : {}) }}
              onClick={() => setHighlightStable((v) => !v)}
            >
              {highlightStable ? "Highlight Givens: On" : "Highlight Givens: Off"}
            </button>
            <button
              style={{ ...styles.btn, ...(showMistakes ? { background: "#ef4444", color: "white", borderColor: "#ef4444" } : {}) }}
              onClick={() => setShowMistakes((v) => !v)}
            >
              {showMistakes ? "Mistakes: On" : "Mistakes: Off"}
            </button>
          </div>
        </div>

        {/* Banner */}
        {filled && (
          <div style={styles.bannerWrap}>
            {solved ? (
              <div style={styles.bannerOk}>✅ Solved! Great job.</div>
            ) : (
              <div style={styles.bannerBad}>❌ Incorrect solution — some rows, columns, or boxes have duplicates.</div>
            )}
          </div>
        )}

        <div style={styles.section}>
          {/* Board */}
          <div ref={boardRef} style={styles.boardOuter} role="application" aria-label="Sudoku board">
            <div style={styles.board}>
              {board.map((row, r) => (
                <React.Fragment key={r}>
                  {row.map((cell, c) => {
                    const isSelected = sr === r && sc === c;
                    const isGiven = cell.given;
                    const key = cellKey(r, c);
                    const isConflict = showMistakes && conflicts.has(key);

                    const base = { ...styles.cellBtn, ...(showGridLines ? thickBorder(r, c) : {}) };
                    if (isSelected) Object.assign(base, styles.selected);
                    if (isGiven && highlightStable) Object.assign(base, styles.givenBg);
                    if (isConflict) Object.assign(base, styles.mistakeRing);

                    const bigNumStyle = { ...styles.bigNum, ...(isGiven ? styles.bigNumGiven : {}) };
                    if (isConflict) bigNumStyle.color = styles.mistakeRing.color;

                    return (
                      <button
                        key={key}
                        onClick={() => setSelected([r, c])}
                        aria-label={`Row ${r + 1} Column ${c + 1}`}
                        style={base}
                      >
                        {cell.value !== 0 ? (
                          <span style={bigNumStyle}>{cell.value}</span>
                        ) : (
                          <Notes maskSet={cell.notes} dim={!isSelected && isGiven && highlightStable} />
                        )}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>

            {/* Numpad */}
            <div style={styles.numpadWrap}>
              {DIGITS.map((n) => (
                <button key={n} style={styles.numpadBtn} onClick={() => onClickDigit(n)}>{n}</button>
              ))}
              <button style={styles.clearBtn} onClick={clearCell}>Clear</button>
              <button style={{ ...styles.pencilBtn, ...(pencil ? styles.btnPrimary : {}) }} onClick={() => setPencil((p) => !p)}>✏️ Pencil</button>
            </div>
          </div>

          {/* Sidebar */}
          <aside style={styles.sidebar}>
            <h2 style={{ margin: 0, fontWeight: 600 }}>Legend</h2>
            <ul style={{ marginTop: 8, paddingLeft: 18, lineHeight: 1.8 }}>
              <li><span style={styles.legendDot("#fde68a", "#f59e0b")} /> &nbsp;Given (stable)</li>
              <li><span style={styles.legendDot("#ffffff", "#e2e8f0")} /> &nbsp;Your entry</li>
              <li><span style={styles.legendDot("#c7d2fe", "#6366f1")} /> &nbsp;Selected</li>
            </ul>
            <p style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
              Tip: ✏️ Pencil mode shows small candidates. Turn it off to place final numbers.
            </p>
          </aside>
        </div>

      </div>
    </div>
  );
}

function Notes({ maskSet, dim = false }) {
  return (
    <div style={styles.notesGrid(dim)}>
      {DIGITS.map((n) => (
        <div key={n} style={styles.noteText(maskSet.has(n))}>{n}</div>
      ))}
    </div>
  );
}